/**
 * Sidebar Mini Flyout Management
 * Handles dropdown flyouts when clicking on submenu items in sidebar-mini state
 */

/* global requestAnimationFrame */

export const initSidebarMiniFlyout = () => {
  const sidebar = document.querySelector('.sidebar');
  if (!sidebar) return;

  let isProcessingClick = false; // Flag to prevent multiple rapid clicks

  // Remove any open dropdown
  const closeDropdown = () => {
    const wrappers = document.querySelectorAll('.sidebar-mini-flyout-wrapper');
    wrappers.forEach((el) => {
      el.style.opacity = '0'; // Fade out smoothly
      setTimeout(() => el.remove(), 150); // Remove after fade
    });
  };

  // Transform submenu to Bootstrap dropdown structure recursively
  const transformSubmenuToDropdown = (submenuNode) => {
    const menu = submenuNode.cloneNode(true);
    menu.classList.remove('submenu');
    menu.classList.add('dropdown-menu');

    menu.querySelectorAll('li').forEach((li) => {
      // Remove nav-item class to avoid custom styles
      li.classList.remove('nav-item');

      const link = li.querySelector('a');
      if (link && link.classList.contains('nav-link')) {
        // Replace nav-link with dropdown-item for default Bootstrap styling
        link.classList.remove('nav-link');
        link.classList.add('dropdown-item');

        // Preserve active state
        if (li.classList.contains('active')) {
          link.classList.add('active');
        }

        // Clean up inner content for default look: extract text, keep icon
        const textSpan = link.querySelector('span');
        if (textSpan) {
          // Move text content to direct child, remove wrapper spans
          const textContent = textSpan.textContent.trim();
          textSpan.remove();
          link.appendChild(document.createTextNode(textContent));
        }

        // Handle icon
        const icon = link.querySelector('i, svg');
        if (icon) {
          icon.classList.add('me-2');
          // Ensure icon is first child
          link.insertBefore(icon, link.firstChild);
        }

        // Remove chevron spans (Bootstrap uses caret in toggle)
        const chevron = link.querySelector('.chevron');
        if (chevron) {
          chevron.remove();
        }

        // Remove any d-flex wrappers or extra spans
        const flexSpans = link.querySelectorAll('.d-flex');
        flexSpans.forEach((span) => span.remove());

        if (li.classList.contains('has-submenu')) {
          li.classList.add('dropdown-submenu', 'dropend');
          link.classList.add('dropdown-toggle');
          link.setAttribute('role', 'button');
          link.setAttribute('tabindex', '0');
          link.setAttribute('aria-expanded', 'false');
          link.href = '#'; // Ensure no navigation on toggle

          // Recursively transform inner submenu
          const innerSubmenu = li.querySelector('.submenu');
          if (innerSubmenu) {
            const innerMenu = transformSubmenuToDropdown(innerSubmenu);
            innerSubmenu.remove();
            li.appendChild(innerMenu);
          }
        }
      }
    });

    return menu;
  };

  // Optimized click handler with better event filtering
  const handleSidebarClick = (event) => {
    // Prevent processing multiple rapid clicks
    if (isProcessingClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const isMini = sidebar.classList.contains('sidebar-mini');
    const clickedLink = event.target.closest('.nav-link');
    const clickedNavItem = event.target.closest('.nav-item');
    const isInsideSidebar = sidebar.contains(event.target);

    // Ignore clicks inside the flyout to allow nested dropdown interactions
    if (event.target.closest('.sidebar-mini-flyout-wrapper')) {
      return;
    }

    // Early exit if not in mini mode or not clicking inside sidebar
    if (!isMini || !isInsideSidebar || !clickedLink || !clickedNavItem) {
      closeDropdown();
      return;
    }

    // Check if this is a child item (inside a submenu)
    const isChildItem = clickedNavItem.closest('.submenu') !== null;
    const hasSubmenu = clickedNavItem.classList.contains('has-submenu');

    // If it's a child item without submenus, allow normal navigation
    if (isChildItem && !hasSubmenu) {
      closeDropdown(); // Clean up any open flyouts
      // Don't prevent default - allow navigation
      return;
    }

    // If it's a parent item with submenus, create flyout
    if (!isChildItem && hasSubmenu) {
      event.preventDefault();
      event.stopPropagation();

      isProcessingClick = true; // Set flag

      // Add a small delay to prevent rapid clicking issues
      setTimeout(() => {
        isProcessingClick = false;
      }, 200);

      closeDropdown(); // Close any existing

      if (typeof window.hideAllTooltips === 'function') {
        window.hideAllTooltips();
      }

      // Get the submenu from the parent submenu item
      const submenu = clickedNavItem.querySelector('.submenu');
      if (!submenu) return;

      // Transform submenu to dropdown structure
      const dropdownMenu = transformSubmenuToDropdown(submenu);
      dropdownMenu.classList.add('show');

      // Create wrapper with smooth appearance
      const wrapper = document.createElement('div');
      wrapper.classList.add('dropdown', 'dropend', 'sidebar-mini-flyout-wrapper');
      wrapper.style.position = 'fixed';
      wrapper.style.top = `${clickedNavItem.getBoundingClientRect().top}px`;
      wrapper.style.left = `${sidebar.getBoundingClientRect().right}px`;
      wrapper.style.zIndex = '1050';
      wrapper.style.pointerEvents = 'auto';
      wrapper.style.opacity = '0'; // Start invisible
      wrapper.style.transition = 'opacity 0.2s ease';

      // Create toggle button
      const toggleBtn = document.createElement('a');
      toggleBtn.href = '#';
      toggleBtn.classList.add('btn', 'btn-link', 'dropdown-toggle');
      toggleBtn.setAttribute('role', 'button');
      toggleBtn.setAttribute('data-bs-toggle', 'dropdown');
      toggleBtn.setAttribute('aria-expanded', 'true');
      toggleBtn.style.display = 'none';

      // Append to wrapper
      wrapper.appendChild(toggleBtn);
      wrapper.appendChild(dropdownMenu);
      document.body.appendChild(wrapper);

      // Fade in smoothly
      requestAnimationFrame(() => {
        wrapper.style.opacity = '1';
      });

      // Init Bootstrap dropdown
      const bsDropdown = new bootstrap.Dropdown(toggleBtn);
      bsDropdown.show();

      // Add hover support for nested submenus
      const submenus = wrapper.querySelectorAll('.dropdown-submenu');
      submenus.forEach((submenu) => {
        const menu = submenu.querySelector('.dropdown-menu');
        if (menu) {
          submenu.addEventListener('mouseenter', () => {
            menu.classList.add('show');
            const toggle = submenu.querySelector('.dropdown-toggle');
            if (toggle) {
              toggle.setAttribute('aria-expanded', 'true');
            }
          });
          submenu.addEventListener('mouseleave', () => {
            menu.classList.remove('show');
            const toggle = submenu.querySelector('.dropdown-toggle');
            if (toggle) {
              toggle.setAttribute('aria-expanded', 'false');
            }
          });
        }
      });
    } else {
      // For any other case, just close flyouts
      closeDropdown();
    }
  };

  // Use a single event listener with proper delegation
  document.addEventListener('click', handleSidebarClick, { passive: false });

  // Close on resize
  window.addEventListener('resize', closeDropdown);

  // Close on escape key
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDropdown();
    }
  });
};
